/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-shell
 * @responsibility Render the filter row with its filter and display popover menus.
 * @state-owner navigation store owns filter values and display options
 * @allowed-imports React; workspace store; shared filters, icons and overlay host
 * @forbidden business rules; network
 * @public-api workspace modules
 * @side-effects opens popovers through the overlay host
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useRef } from 'react';
import { DISPLAY_PROPERTY_OPTIONS, defaultDisplay, type FilterDefinition } from '../lib/filters';
import { useTranslator } from '../model/use-workspace';
import { type DisplayOptions, useWorkspaceUiStore } from '../model/workspace-store';
import { useOverlay } from './OverlayHost';
import { DisplayIcon, FilterIcon } from './VispoIcon';

export function useFilterValues(scope: string): (key: string) => readonly string[] {
  const filters = useWorkspaceUiStore((state) => state.filters[scope]);
  return (key: string) => filters?.[key] ?? [];
}

export function useDisplayOptions(scope: string): DisplayOptions {
  const stored = useWorkspaceUiStore((state) => state.display[scope]);
  return stored ?? defaultDisplay(scope);
}

export function FilterBar({
  scope,
  definitions,
  showDisplay = true,
}: {
  scope: string;
  definitions: readonly FilterDefinition[];
  showDisplay?: boolean;
}) {
  const t = useTranslator();
  const { openPopover } = useOverlay();
  const filters = useWorkspaceUiStore((state) => state.filters[scope]);
  const setFilter = useWorkspaceUiStore((state) => state.setFilter);
  const clearFilter = useWorkspaceUiStore((state) => state.clearFilter);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const displayButtonRef = useRef<HTMLButtonElement>(null);

  const valuesOf = (key: string) => filters?.[key] ?? [];
  const labelOf = (key: string, value: string) => {
    const definition = definitions.find((item) => item.k === key);
    return definition?.o.find((option) => option[0] === value)?.[1] ?? value;
  };

  const chips = definitions.filter((definition) => valuesOf(definition.k).length > 0);

  const openFilterMenu = (openKey?: string) => {
    const rect = filterButtonRef.current?.getBoundingClientRect();
    const x = Math.max(14, rect?.left ?? 400);
    const y = (rect?.bottom ?? 120) + 8;
    const definition = openKey ? definitions.find((item) => item.k === openKey) : undefined;
    const content = definition ? (
      <>
        <button className="mit2" type="button" onClick={() => openFilterMenu()}>
          {`‹ ${t(definition.t)}`}
        </button>
        <div className="mdiv" />
        {definition.o.map(([value, label]) => {
          const on = valuesOf(definition.k).includes(value);
          return (
            <button
              className="mit2"
              key={value}
              type="button"
              onClick={() => {
                const current = valuesOf(definition.k);
                const next = on ? current.filter((item) => item !== value) : [...current, value];
                setFilter(scope, definition.k, next);
                openFilterMenu(definition.k);
              }}
            >
              <span className={`vck2 ${on ? 'on' : ''}`}>{on ? '✓' : ''}</span>
              {t(label)}
            </button>
          );
        })}
      </>
    ) : (
      <>
        <div className="vfhd">
          {t('篩選條件')}
          <span className="cmdk">
            <kbd>F</kbd>
          </span>
        </div>
        {definitions.map((item) => (
          <button
            className="mit2"
            key={item.k}
            type="button"
            onClick={() => openFilterMenu(item.k)}
          >
            {t(item.t)}
            {valuesOf(item.k).length ? (
              <span className="vfn2">{valuesOf(item.k).length}</span>
            ) : null}
            <span className="vfar">›</span>
          </button>
        ))}
      </>
    );
    openPopover({ content, x, y, className: 'cmenu rmenu vfmenu' });
  };

  return (
    <div className="vfrow">
      {chips.map((definition) => {
        const values = valuesOf(definition.k);
        return (
          <span className="vfc" key={definition.k}>
            <button className="vfcm" type="button" onClick={() => openFilterMenu(definition.k)}>
              <span className="vfck">{t(definition.t)}</span>
              <span className="vfcv">
                {values.length > 2
                  ? `${values.length} ${t('項')}`
                  : values.map((value) => labelOf(definition.k, value)).join(', ')}
              </span>
            </button>
            <button className="vfcx" type="button" onClick={() => clearFilter(scope, definition.k)}>
              ×
            </button>
          </span>
        );
      })}
      {definitions.length ? (
        <button
          className="vfadd"
          id="fltbtn"
          ref={filterButtonRef}
          type="button"
          onClick={() => openFilterMenu()}
        >
          <FilterIcon />
          <span>{chips.length ? '' : t('篩選')}</span>
        </button>
      ) : null}
      {chips.length > 1 ? (
        <button className="vfclr" type="button" onClick={() => clearFilter(scope)}>
          {t('全部清除')}
        </button>
      ) : null}
      <div className="sp" />
      {showDisplay ? <DisplayButton buttonRef={displayButtonRef} scope={scope} /> : null}
    </div>
  );
}

function DisplayButton({
  scope,
  buttonRef,
}: {
  scope: string;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const t = useTranslator();
  const { openPopover } = useOverlay();
  const options = useDisplayOptions(scope);
  const setDisplay = useWorkspaceUiStore((state) => state.setDisplay);
  const boardScope = scope === 'act' || scope === 'sact';

  const open = (current: DisplayOptions) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    const x = Math.max(14, (rect?.right ?? window.innerWidth - 40) - 300);
    const y = (rect?.bottom ?? 120) + 8;
    const apply = (patch: Partial<DisplayOptions>) => {
      const next = { ...current, ...patch };
      setDisplay(scope, next);
      open(next);
    };
    openPopover({
      x,
      y,
      className: 'cmenu vdmenu',
      content: (
        <div className="vdisp">
          {boardScope ? (
            <div className="vseg" style={{ width: '100%', margin: '0 0 12px' }}>
              {(
                [
                  ['list', '清單'],
                  ['board', '看板'],
                ] as const
              ).map(([value, label]) => (
                <button
                  className={current.view === value ? 'on' : ''}
                  key={value}
                  style={{ flex: 1 }}
                  type="button"
                  onClick={() => apply({ view: value })}
                >
                  {t(label)}
                </button>
              ))}
            </div>
          ) : null}
          {boardScope ? (
            <div className="vdrow">
              <span>{t('分組')}</span>
              <select
                onChange={(event) => apply({ grp: event.target.value as DisplayOptions['grp'] })}
                value={current.grp}
              >
                <option value="st">{t('狀態')}</option>
                <option value="ci">{t('班級')}</option>
              </select>
            </div>
          ) : null}
          <div className="vdrow">
            <span>{t('排序')}</span>
            <select
              onChange={(event) => apply({ ord: event.target.value as DisplayOptions['ord'] })}
              value={current.ord}
            >
              <option value="upd">{t('更新時間')}</option>
              <option value="new">{t('建立時間')}</option>
              <option value="name">{t('名稱')}</option>
              <option value="st">{t('狀態')}</option>
              <option value="tag">{t('標籤')}</option>
            </select>
          </div>
          <div className="mdiv" />
          <div className="vdlab">{t('顯示屬性')}</div>
          <div className="vdprops">
            {DISPLAY_PROPERTY_OPTIONS.map(([value, label]) => (
              <button
                className={`vdp ${current.props.includes(value) ? 'on' : ''}`}
                key={value}
                type="button"
                onClick={() =>
                  apply({
                    props: current.props.includes(value)
                      ? current.props.filter((item) => item !== value)
                      : [...current.props, value],
                  })
                }
              >
                {t(label)}
              </button>
            ))}
          </div>
        </div>
      ),
    });
  };

  return (
    <button
      className="vtib"
      ref={buttonRef}
      title={t('顯示方式')}
      type="button"
      onClick={() => open(options)}
    >
      <DisplayIcon />
    </button>
  );
}
